import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import Monogram from "@/components/Monogram";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Connexion réussie");
      navigate("/", { replace: true });
    } catch (e) {
      const msg = e?.response?.data?.detail || "Identifiants invalides";
      setErr(typeof msg === "string" ? msg : "Erreur de connexion");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[var(--bg-app)]">
      {/* Left: brand + image */}
      <div
        className="hidden lg:flex relative border-r border-[var(--border-default)] overflow-hidden"
        data-testid="login-hero"
      >
        <div
          className="absolute inset-0 bg-cover bg-center opacity-60 grayscale"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1778731525532-d2ab833c8463?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwZWRpdG9yaWFsJTIwb2ZmaWNlJTIwZGVza3xlbnwwfHx8fDE3ODMyODcyODN8MA&ixlib=rb-4.1.0&q=85')",
          }}
        />
        <div className="absolute inset-0 bg-[var(--bg-app)] opacity-30" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div>
            <Monogram size={72} className="mb-6" />
            <div className="caption mb-3">Outil interne · presse</div>
            <div className="font-headings text-6xl leading-none tracking-tight">
              AIMKA
            </div>
            <div className="mt-4 font-mono text-xs uppercase tracking-widest text-black/70">
              Brouillons automatisés · aucune publication
            </div>
          </div>
          <div className="max-w-md">
            <div className="w-8 h-0.5 bg-black mb-4" />
            <p className="text-sm leading-relaxed text-black/80">
              Surveille les flux, rédige en français, dépose en brouillon.
              Aucune diffusion sans relecture humaine.
            </p>
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-8">
        <form
          onSubmit={onSubmit}
          className="w-full max-w-sm"
          data-testid="login-form"
        >
          <div className="mb-8 lg:hidden flex items-center gap-3">
            <Monogram size={44} />
            <div className="font-headings text-3xl tracking-tight">AIMKA</div>
          </div>
          <div className="mb-8">
            <div className="caption mb-2">Accès rédaction</div>
            <h1 className="font-headings text-3xl tracking-tight">Connexion</h1>
            <p className="text-sm text-[var(--text-secondary)] mt-2">
              Espace strictement interne. Aucune session partagée.
            </p>
          </div>

          <div className="space-y-5">
            <div>
              <label className="block caption mb-2">Adresse e-mail</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                className="w-full border border-[var(--border-default)] px-3 py-2.5 bg-white font-mono text-sm rounded-none"
              />
            </div>
            <div>
              <label className="block caption mb-2">Mot de passe</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                className="w-full border border-[var(--border-default)] px-3 py-2.5 bg-white font-mono text-sm rounded-none"
              />
            </div>

            {err && (
              <div
                className="border border-[var(--status-error)] px-3 py-2 text-xs font-mono text-[var(--status-error)]"
                data-testid="login-error"
              >
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full bg-black text-white py-3 text-sm font-medium uppercase tracking-[0.15em] hover:bg-[#333] transition-colors rounded-none disabled:opacity-60"
            >
              {loading ? "Connexion…" : "Entrer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
